/**
 * App Layout Component
 *
 * Custom layout matching elSDK G&S structure:
 * - Sidebar with logo, navigation, footer (no close/X button)
 * - Top bar with breadcrumbs + UserInfo
 * - PageProvider + PageContextBridge for page context
 */

import React, { useState, useEffect, useRef, Suspense } from 'react'

import { PageContext } from '@elsapiens/layout'
import { PageProvider, usePage, useTheme, useShortcuts } from '@elsapiens/providers'
import { cn } from '@elsapiens/utils'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { CANVAS_IMPORTS, BACKGROUND_CLASS_MAP } from './layout-helpers'
import { navItems } from './nav-items'
import { Quickbar } from './Quickbar'
import { SidebarFooterContent } from './SidebarFooter'
import { SidebarNav } from './SidebarNav'
import { TopBarContent } from './TopBar'
import { getCurrentWorkspace } from '../../services/workspace-service'
import { FavoritesProvider } from '../../stores/favorites-store'

const logoImage = '/logo.svg'

const ContentLoader = (): React.JSX.Element => (
  <div className="flex items-center justify-center h-full min-h-[50vh]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto el-mb-md" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

function PageContextBridge({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { pageTitle, pageDescription, breadcrumbs } = usePage()
  return (
    <PageContext.Provider value={{ pageTitle, pageDescription, breadcrumbs }}>
      {children}
    </PageContext.Provider>
  )
}

function SidebarLogo(): React.JSX.Element {
  return (
    <div className="flex items-center el-gap-md">
      <img alt="elBooks" className="h-8 w-auto" src={logoImage} />
      <span className="font-semibold text-lg text-foreground">elBooks</span>
    </div>
  )
}

function useWorkspaceName(): string | null {
  const [workspaceName, setWorkspaceName] = useState<string | null>(null)
  useEffect(() => {
    void getCurrentWorkspace().then(ws => { if (ws) { setWorkspaceName(ws.name) } })
  }, [])
  return workspaceName
}

function DesktopSidebar(): React.JSX.Element {
  const workspaceName = useWorkspaceName()
  return (
    <aside className="hidden lg:flex el-sidebar">
      <div
        className="flex items-center el-px-md border-b border-border"
        style={{ height: 'var(--topbar-height)' }}
      >
        <SidebarLogo />
      </div>
      {workspaceName && (
        <div className="el-py-md" style={{ paddingInline: 'calc(var(--el-layout-gap-sm) + var(--sidebar-item-padding-x))' }}>
          <span className="text-xs font-bold text-muted-foreground truncate block">{workspaceName}</span>
        </div>
      )}
      <SidebarNav items={navItems} />
      <div className="border-t border-border el-p-sm">
        <SidebarFooterContent />
      </div>
    </aside>
  )
}

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

function MobileSidebar({ isOpen, onClose }: MobileSidebarProps): React.JSX.Element {
  const workspaceName = useWorkspaceName()
  return (
    <aside
      className={cn(
        'lg:hidden fixed inset-y-0 left-0 z-40 el-sidebar transform transition-transform duration-200 ease-in-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div
        className="flex items-center justify-between el-px-md border-b border-border"
        style={{ height: 'var(--topbar-height)' }}
      >
        <SidebarLogo />
        <button
          className="el-p-xs rounded-md hover:bg-muted transition-colors"
          onClick={onClose}
        >
          <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </button>
      </div>
      {workspaceName && (
        <div className="el-py-md" style={{ paddingInline: 'calc(var(--el-layout-gap-sm) + var(--sidebar-item-padding-x))' }}>
          <span className="text-xs font-bold text-muted-foreground truncate block">{workspaceName}</span>
        </div>
      )}
      <SidebarNav items={navItems} onItemClick={onClose} />
      <div className="border-t border-border el-p-sm">
        <SidebarFooterContent />
      </div>
    </aside>
  )
}

interface MobileOverlayProps {
  isOpen: boolean
  onClose: () => void
}

function MobileOverlay({ isOpen, onClose }: MobileOverlayProps): React.JSX.Element | null {
  if (!isOpen) { return null }
  return (
    <div
      aria-hidden="true"
      className="lg:hidden fixed inset-0 z-30 bg-black/50"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') { onClose() } }}
    />
  )
}

function MobileMenuButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      className="lg:hidden el-p-xs rounded-md hover:bg-muted transition-colors flex-shrink-0"
      onClick={onClick}
    >
      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
    </button>
  )
}

function useLayoutEffects(
  backgroundStyle: string,
  mainRef: React.RefObject<HTMLElement>,
  pathname: string,
  setMobileMenuOpen: (v: boolean) => void,
): void {
  useEffect(() => {
    const loader = CANVAS_IMPORTS[backgroundStyle as keyof typeof CANVAS_IMPORTS]
    if (loader) { void loader() }
  }, [backgroundStyle])

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [pathname, mainRef])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname, setMobileMenuOpen])
}

function useGlobalShortcuts(): void {
  const navigate = useNavigate()
  const { register } = useShortcuts()
  useEffect(() => {
    const cleanups = [
      register({ id: 'nav-home',    keys: 'alt+h', label: 'Dashboard',  description: 'Go to Dashboard',  category: 'Navigation', scope: 'global', action: () => navigate('/') }),
      register({ id: 'nav-acct',     keys: 'alt+a', label: 'Accounting', description: 'Go to Accounting', category: 'Navigation', scope: 'global', action: () => navigate('/accounting') }),
      register({ id: 'nav-entities', keys: 'alt+t', label: 'Entities',   description: 'Go to Entities',   category: 'Navigation', scope: 'global', action: () => navigate('/entities') }),
      register({ id: 'nav-sales',   keys: 'alt+s', label: 'Sales',      description: 'Go to Sales',      category: 'Navigation', scope: 'global', action: () => navigate('/sales') }),
      register({ id: 'nav-purch',   keys: 'alt+p', label: 'Purchases',  description: 'Go to Purchases',  category: 'Navigation', scope: 'global', action: () => navigate('/purchases') }),
      register({ id: 'nav-banking', keys: 'alt+b', label: 'Banking',    description: 'Go to Banking',    category: 'Navigation', scope: 'global', action: () => navigate('/banking') }),
      register({ id: 'nav-reports', keys: 'alt+r', label: 'Reports',    description: 'Go to Reports',    category: 'Navigation', scope: 'global', action: () => navigate('/reports') }),
      register({ id: 'nav-period',  keys: 'alt+e', label: 'Period End',      description: 'Go to Period End',      category: 'Navigation', scope: 'global', action: () => navigate('/period-end') }),
      register({ id: 'nav-admin',   keys: 'alt+m', label: 'Administration', description: 'Go to Administration', category: 'Navigation', scope: 'global', action: () => navigate('/admin') }),
      register({
        id: 'global-escape-blur',
        keys: 'escape',
        label: 'Lose Focus',
        description: 'Blur the focused input field',
        category: 'Navigation',
        scope: 'global',
        priority: 50,
        enabled: () => {
          const el = document.activeElement as HTMLElement | null
          return !!(el && el !== document.body && (
            el.tagName === 'INPUT' ||
            el.tagName === 'TEXTAREA' ||
            el.isContentEditable
          ))
        },
        action: () => { (document.activeElement as HTMLElement).blur() },
      }),
    ]
    return () => cleanups.forEach(fn => fn())
  }, [register, navigate])
}

export function AppLayout(): React.JSX.Element {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const mainRef = useRef<HTMLElement>(null)
  const { backgroundStyle } = useTheme()
  const backgroundClass = BACKGROUND_CLASS_MAP[backgroundStyle] ?? ''

  useLayoutEffects(backgroundStyle, mainRef, location.pathname, setMobileMenuOpen)
  useGlobalShortcuts()

  return (
    <PageProvider>
      <PageContextBridge>
        <FavoritesProvider>
          <div className="flex h-screen bg-background">
            <DesktopSidebar />
            <MobileSidebar isOpen={isMobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
            <MobileOverlay isOpen={isMobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="sticky top-0 z-20 flex flex-col">
                <header className="el-topbar el-surface-colored">
                  <div className="el-px-md flex items-center flex-1 min-w-0 gap-2">
                    <MobileMenuButton onClick={() => setMobileMenuOpen(true)} />
                    <TopBarContent />
                  </div>
                </header>
                <Quickbar />
              </div>

              <main ref={mainRef} className={cn('flex-1 overflow-y-auto overflow-x-hidden flex flex-col', backgroundClass)}>
                <div className="flex flex-col flex-1 h-full">
                  <Suspense fallback={<ContentLoader />}>
                    <Outlet />
                  </Suspense>
                </div>
              </main>
            </div>
          </div>
        </FavoritesProvider>
      </PageContextBridge>
    </PageProvider>
  )
}
